/* eslint-disable no-nested-ternary */
import styled, { css } from 'styled-components/macro';
import { TabShapes } from './tab-consts';

const trackWrapper = css`
  gap: 1px;
  overflow: hidden;
  border: var(--tab-track-border, 1px var(--platinum1) solid);
  border-radius: var(--tab-radius, 10px);
  /* Shows through the 1px gap as the segment separator. */
  background-color: var(--tab-track-bg, var(--platinum1));
`;

export const TabsWrapper = styled.div.attrs({ className: 'dsc-tabs' })`
  display: flex;
  flex-direction: row;

  ${({ $shape }) =>
    $shape === TabShapes.FILLED || $shape === TabShapes.VIEW_SWITCHER
      ? trackWrapper
      : css`
          gap: 16px;
          text-transform: var(--title-text-transform);
        `}

  ${({ $shape }) =>
    $shape === TabShapes.VIEW_SWITCHER &&
    css`
      width: fit-content;
    `}

  ${({ $shape }) =>
    $shape === TabShapes.FILTER &&
    css`
      gap: 0;
      text-transform: capitalize;
      border: var(--tab-filter-track-border, none);
      border-radius: var(--tab-radius, 10px);
      overflow: hidden;
    `}
`;

const dummyPlaceholder = css`
  ::after {
    height: 0;
    content: '';
    visibility: hidden;
    overflow: hidden;
    user-select: none;
    pointer-events: none;
    font-weight: 700;
  }
`;

export const TabWrapper = styled.div.attrs(({ $selected }) => ({ className: `dsc-tab ${$selected ? 'active' : ''}` }))`
  position: relative;
  font-size: 14px;
  letter-spacing: 0.5px;
  height: 32px;
  cursor: ${({ disabled }) => !disabled && 'pointer'};
  display: grid;
  align-items: end;
  box-sizing: border-box;
  transition: color 0.2s ease-in-out;
  ${dummyPlaceholder};

  ${({ $shape, $selected, disabled }) =>
    ($shape === TabShapes.OUTLINED &&
      css`
        padding: 0 16px;
        border-radius: var(--tab-radius, 10px);
        color: ${disabled ? 'var(--tab-disabled-color)' : 'var(--tab-outline-color)'};
        border: 1px solid transparent;
        font-weight: var(--tab-outline-font-weight, inherit);

        ${$selected &&
        !disabled &&
        css`
          color: var(--tab-outline-active-color);
          border: 1px solid var(--tab-outline-active-color);
          background-color: var(--tab-active-bg-color);
          font-weight: var(--tab-outline-font-weight-selected);
        `}
        &:hover {
          color: ${!disabled && 'var(--tab-outline-active-color)'};
        }
      `) ||
    ($shape === TabShapes.FLAT &&
      css`
        padding: 0 12px;
        transition: border-bottom-width 0.1s;
        color: ${disabled ? 'var(--tab-disabled-color)' : 'var(--tab-flat-color)'};

        border-bottom: var(--tab-flat-border-bottom, 1px solid transparent);

        ${!disabled &&
        ($selected
          ? css`
              color: var(--tab-flat-active-color);
              /* Fallback stays inline: the active colour is set per theme class, so a :root default would freeze it. */
              border-bottom: var(
                --tab-flat-border-bottom-selected,
                var(--tab-flat-border-bottom-width-selected) solid var(--tab-flat-active-color)
              );
              font-weight: var(--tab-flat-font-weight-selected);
            `
          : css`
              &:hover {
                border-bottom: 1px solid var(--tab-flat-active-color);
              }
            `)}
      `) ||
    ($shape === TabShapes.FILLED &&
      css`
        height: 48px;
        min-width: 50px;
        padding: 0 16px;
        white-space: nowrap;
        flex-grow: 1;
        text-align: center;
        border: ${!disabled && $selected
          ? 'var(--tab-segment-border-selected, none)'
          : 'var(--tab-segment-border, none)'};

        ${!disabled && $selected
          ? css`
              color: var(--tab-segment-color-selected, var(--darkSlateGray3));
              background-color: var(--tab-segment-bg-selected, var(--platinum1));
            `
          : css`
              color: ${disabled
                ? 'var(--tab-segment-color-disabled, var(--timberwolf))'
                : 'var(--tab-segment-color, var(--cadetGray))'};
              background-color: var(--tab-segment-bg, var(--white));
              ${!disabled &&
              css`
                &:hover {
                  background-color: var(--tab-segment-bg-hover, var(--antiFlashWhite2));
                }
              `}
            `}

        /* The track clips at its inner radius (outer minus its 1px border); end segments follow it
           so a selected segment's border isn't cut off at the corners. */
        &:first-child {
          border-radius: calc(var(--tab-radius, 10px) - 1px) 0 0 calc(var(--tab-radius, 10px) - 1px);
        }
        &:last-child {
          border-radius: 0 calc(var(--tab-radius, 10px) - 1px) calc(var(--tab-radius, 10px) - 1px) 0;
        }
        &:only-child {
          border-radius: calc(var(--tab-radius, 10px) - 1px);
        }
      `) ||
    ($shape === TabShapes.FILTER &&
      css`
        min-width: ${({ $minWidth }) => ($minWidth ? `${$minWidth}px` : '50px')};
        height: unset;
        padding: 10px;
        white-space: nowrap;
        text-align: center;
        font-weight: ${$selected ? '600' : '400'};
        color: ${$selected && !disabled
          ? 'var(--tab-filter-active-color, var(--midnight))'
          : 'var(--tab-filter-color, var(--antiFlashWhite2))'};
        background-color: ${disabled
          ? 'var(--tab-filter-bg, var(--darkSlateGray2))'
          : $selected
          ? 'var(--tab-filter-active-bg, var(--mainGreen))'
          : 'var(--tab-filter-bg, var(--midnight))'};
        ${$selected &&
        !disabled &&
        css`
          border-radius: var(--tab-radius, 0);
        `}
        /* Inset ring, not a border: the separator's border-right would cut its right edge. */
        box-shadow: ${$selected && !disabled
          ? 'var(--tab-filter-box-shadow-selected, none)'
          : 'var(--tab-filter-box-shadow, none)'};
        transition: all 0.2s ease-in-out;
        cursor: ${disabled ? 'default' : 'pointer'};

        :hover {
          background-color: ${!$selected && !disabled && 'var(--tab-filter-hover-bg, var(--darkSlateGray2))'};
        }

        :not(:last-child) {
          border-right: 1px solid var(--tab-filter-separator, var(--mainGreen));
        }
      `) ||
    ($shape === TabShapes.VIEW_SWITCHER &&
      css`
        padding: 0 8px;
        border: ${$selected && !disabled
          ? 'var(--tab-segment-border-selected, 1px solid var(--midnightGreen))'
          : 'var(--tab-segment-border, 1px solid transparent)'};

        ${!disabled && $selected
          ? css`
              color: var(--tab-segment-color-selected, var(--darkSlateGray3));
              background-color: var(--tab-segment-bg-selected, var(--platinum1));
            `
          : css`
              color: ${disabled
                ? 'var(--tab-segment-color-disabled, var(--timberwolf))'
                : 'var(--tab-segment-color, var(--cadetGray))'};
              background-color: var(--tab-segment-bg, var(--white));
              &:hover {
                background-color: ${!disabled && 'var(--tab-segment-bg-hover, var(--antiFlashWhite2))'};
              }
            `}

        /* Must match the track's radius, or the track bg shows in the segment corners. */
        &:first-child {
          border-radius: var(--tab-radius, 10px) 0 0 var(--tab-radius, 10px);
        }
        &:last-child {
          border-radius: 0 var(--tab-radius, 10px) var(--tab-radius, 10px) 0;
        }
        &:only-child {
          border-radius: var(--tab-radius, 10px);
        }
      `)}
`;
